/**
 * Finds an object in an array or object by a given key and value.
 * @param {Array|Object} collection - The collection to search through (can be array or object).
 * @param {String} key - The key to search for.
 * @param {*} value - The value to match against.
 * @returns {Object|null} - The found object, or null if not found.
 */
export function findObjectByKeyValue(collection, key, value) {
   // Handle array case
   if (Array.isArray(collection)) {
      for (let i = 0; i < collection.length; i++) {
         if (collection[i] && collection[i][key] === value) {
            return collection[i]
         }
      }
   }
   // Handle object case
   else if (collection && typeof collection === 'object') {
      for (const obj of Object.values(collection)) {
         if (obj && obj[key] === value) {
            return obj
         }
      }
   }

   return null
}

export function ifZeroFloatReturnEmptyString(aString) {
   if (aString != 0.0) {
      return aString
   } else {
      return ''
   }
}
/**
 * Formats a string as a currency with the given currency object.
 *
 * @param {*} aString - The string to format.
 * @param {*} currencyObject - The currency object to use for formatting.
 * @param {*} showZero - Whether to show zero values as the currency symbol.
 * @returns {String} - The formatted currency string.
 */
export function formatFloatAsCurrencySymbol(
   aString,
   currencyObject,
   showZero = false,
) {
   if (
      aString === null ||
      (!showZero && ifZeroFloatReturnEmptyString(aString) == '')
   ) {
      return ''
   }
   let number = showZero
      ? parseFloat(aString)
      : parseFloat(ifZeroFloatReturnEmptyString(aString))

   let parts = number.toFixed(2).split('.')
   parts[0] = parts[0].replace(
      /\B(?=(\d{3})+(?!\d))/g,
      currencyObject.Thousand_separator
         ? currencyObject.Thousand_separator
         : ' ',
   ) // Add thousand separator

   // If the currency is EUR, it will use the symbol after the number. Otherwise, it will use the symbol before the number.
   const formattedNumber =
      currencyObject.Code === 'EUR'
         ? parts.join(currencyObject.Decimal_separator) +
           ' ' +
           currencyObject.Symbol
         : currencyObject.Symbol + parts.join(currencyObject.Decimal_separator) // Combine with custom decimal separator
   return formattedNumber // Combine with custom decimal separator
}

/**
 * Converts a USD value to the value in the given currency object.
 *
 * @param {*} USD - The USD value to convert.
 * @param {*} currencyObject - The currency object to use for the conversion.
 * @returns {Number} - The converted value.
 */
export function convertToCurrency(USD, currencyObject) {
   if (USD === null) {
      return 0
   }
   let number = parseFloat(USD)
   return number * currencyObject.Rate
}

// If "0.00" is entered, will return empty string. If "0.01" or greater is entered, returns the "$" + value entered.
/**
 * Formats a float value as a percentage string
 *
 * @param {*} aString - The value to format.
 * @param {number} precision - The number of decimal places to show. Defaults to 2.
 * @returns {String} - The formatted percentage string.
 */
export function formatFloatAsPercent(aString, precision = 2) {
   if (ifZeroFloatReturnEmptyString(aString) == '') {
      return ''
   } else if (isNaN(aString)) {
      return ''
   } else
      return (
         Number.parseFloat(ifZeroFloatReturnEmptyString(aString)).toFixed(
            precision,
         ) + '%'
      )
}

// Deeply checks if objects are the same.
export function areObjectsEqual(obj1, obj2) {
   if (obj1 === obj2) {
      return true
   }

   if (
      typeof obj1 !== 'object' ||
      obj1 === null ||
      typeof obj2 !== 'object' ||
      obj2 === null
   ) {
      return false
   }

   const keys1 = Object.keys(obj1)
   const keys2 = Object.keys(obj2)

   if (!areArraysEqual(keys1, keys2)) {
      return false
   }

   for (const key of keys1) {
      if (!keys2.includes(key)) {
         return false
      }

      if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
         if (!areObjectsEqual(obj1[key], obj2[key])) {
            return false
         }
      } else if (obj1[key] !== obj2[key]) {
         return false
      }
   }

   return true
}

function areArraysEqual(arr1, arr2) {
   if (arr1.length !== arr2.length) {
      return false
   }
   for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
         return false
      }
   }
   return true
}

export function removeDiscountNameVariables(discountName) {
   if (discountName?.includes('$PRODUCTNAME')) {
      return discountName.replace('$PRODUCTNAME ', '')
   } else return discountName
}

/**
 * Adds an empty product to the product list if it doesn't already exist
 * @param {Record<string, any>} product_list - The product list object to add to
 * @param {string} productCode - The product code to add
 * @returns {void}
 */
export function addEmptyProductToProductList(product_list, productCode) {
   // If the product list exists and the product code is in the list, return
   if (product_list && product_list.hasOwnProperty(productCode)) {
      return
   }

   // Create an empty product object with the given product code and add it to the product list
   const emptyProduct = {
      Id: 100,
      BookId: 0,
      EmpLow: 0,
      EmpHigh: 0,
      UpdateDt: null,
      BasePrice: 0,
      CreatedAt: '2024-07-18 22:55:44',
      ProductId: 0,
      UpdatedAt: '2024-09-06 18:22:31',
      BillingFreq: '',
      ProductCode: productCode,
      ProductDesc: '',
      ProductName: '',
      VolumeCurve: 0,
      DisplayOrder: 1,
      EmpListPrice: 0,
      SFProduct2Id: '',
      ProductFamily: '',
      MinimumEmployees: 0,
      MinimumListPrice: 0,
      ProductQuoteDesc: '',
      SFPricebookEntryID: '',
      PreviousExpectedMRRFields: 'Empty__c',
      MostRecentExpectedMRRFields: 'Empty__c',
      PreviousCommissionableMRRFields: 'Empty__c',
      MostRecentCommissionableMRRFields: 'Empty__c',
      NotInPricebook: true,
   }
   product_list[productCode] = emptyProduct
}

/**
 * Determines if the book id is a foreign currency pricebook.
 *
 * @param {Integer} book_id
 * @returns {Boolean} Returns true if the book id is a foreign currency pricebook, else false.
 */
export function IsForeignCurrencyQuote(book_id) {
   const id = Number(book_id)
   return id > 200 && id % 100 !== 0
}

// Products that are always billed in USD regardless of pricebook currency.
// These products are billed with payroll and should appear in the payroll USD section on foreign currency quotes.
const ALWAYS_USD_PRODUCT_CODES = new Set([
   'managedPayrollServices',
   'managedPayrollServicesStates',
])

/**
 * Determines if a product is always billed in USD regardless of pricebook currency.
 *
 * @param {string} productCode
 * @returns {boolean} Returns true if the product is always billed in USD.
 */
export function isAlwaysUSD(productCode) {
   return ALWAYS_USD_PRODUCT_CODES.has(productCode)
}

/**
 * Formats a string for Salesforce rich text by converting HTML special characters to HTML entities.
 *
 * @param {string} inputString - The string to format.
 * @returns {string} - The formatted string.
 */
export function formatForSalesforceRichText(inputString) {
   // Ensure input is a string. If not, convert it.
   if (typeof inputString !== 'string') {
      inputString = String(inputString)
   }

   let formattedString = inputString

   // 1. Handle HTML special characters by converting them to HTML entities
   //    Order matters: "&" must be replaced first to avoid double-escaping
   //    e.g., if you replace '"' first, then "&quot;" would become "&amp;quot;"
   formattedString = formattedString
      .replace(/&/g, '&amp;') // Must be done first!
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/%/g, '&percnt;')

   // 2. Handle newlines first, replacing them with HTML break tags
   formattedString = formattedString.replace(/\n/g, '<br/>')

   // 3. Handle tabs: Replace them with non-breaking spaces for consistent spacing
   //    You could use `&nbsp;&nbsp;&nbsp;&nbsp;` for a wider tab, or just a single space.
   //    `&nbsp;` ensures the space is preserved and not collapsed by HTML.
   formattedString = formattedString.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;') // Or just '&nbsp;' for a single non-breaking space

   return formattedString
}

/**
 * Formats a number into a discount percentage value.
 *
 * @param {number} discountPercent - The discount percentage to format.
 * @param {number} decimalPlaces - The number of decimal places to use.
 * @returns {string} The formatted discount percentage as a string with a '%' symbol.
 */
export function formatDiscountPercent(discountPercentage, decimalPlaces = 2) {
  if(discountPercentage <= 0)
      return ("");
  else
      return(discountPercentage.toFixed(decimalPlaces)+'%');
}



/**
 * Gets the quantity of a specific item from the account snowflake object.
 * Given the product code, it will return the value of the item.
 *
 * @param {Object} accountSnowflake - The account snowflake object.
 * @param {string} productCode - The product code to get the value of.
 * @returns {any} The value of the item.
 */
const SNOWFLAKE_QTY_KEY_MAP = {
   "core1": "Billed_Core__c",
   "core2": "Billed_Pro__c",
   "core3": "Billed_Elite__c",
   "benAdmin": "Billed_Ben_Admin__c",
   "performanceMan": "Billed_Performance__c",
   "timeTracking": "Billed_Time_Tracking__c",
}

export function getAccountSnowflakeQtyItem(accountSnowflake, productCode) {
   if (!productCode) return 0
   const key = SNOWFLAKE_QTY_KEY_MAP[productCode];
   return key !== undefined ? (accountSnowflake[key] ?? 0) : 0;
}

export function hasAccountSnowflakeQtyItem(productCode) {
   return !!productCode && productCode in SNOWFLAKE_QTY_KEY_MAP
}